import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { useLang } from "../i18n/LangContext";
import { SectionHead } from "./Reveal";

export const Services = () => {
  const { t } = useLang();
  const [active, setActive] = useState(0);
  const groups = t.services_data;
  const g = groups[active] ?? groups[0];

  return (
    <section id="services" data-testid="services-section" className="px-6 md:px-12 py-28 md:py-40">
      <SectionHead id="services" over={t.services.over} title={t.services.title} title2={t.services.title2} sub={t.services.sub} />
      <div className="mt-20 grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-4 flex lg:flex-col gap-2 overflow-x-auto lg:overflow-visible -mx-6 px-6 lg:mx-0 lg:px-0" role="tablist">
          {groups.map((grp, i) => (
            <button
              key={grp.key}
              role="tab"
              aria-selected={active === i}
              data-testid={`services-tab-${grp.key}`}
              onClick={() => setActive(i)}
              className={`relative shrink-0 text-left px-5 lg:px-0 py-3 lg:py-6 lg:border-b border-line transition-colors duration-300 ${active === i ? "text-[var(--bone)]" : "text-stone hover:text-[var(--bone)]"}`}
            >
              <span className="font-mono text-xs text-gold mr-4">0{i + 1}</span>
              <span className="font-display text-xl lg:text-3xl">{grp.label}</span>
              {active === i && <motion.span layoutId="tab-line" className="absolute left-0 right-0 bottom-0 lg:bottom-[-1px] h-px bg-[var(--gold)]" />}
            </button>
          ))}
        </div>
        <div className="lg:col-span-7 lg:col-start-6">
          <motion.p key={`intro-${g.key}`} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="font-display italic text-2xl md:text-3xl text-[var(--bone)] mb-10">
            {g.intro}
          </motion.p>
          <ul className="border-t border-line" data-testid={`services-panel-${g.key}`}>
            {g.items.map((it, i) => (
              <motion.li
                key={`${g.key}-${it.name}`}
                initial={{ opacity: 0, x: 24 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: i * 0.07 }}
                className="border-b border-line"
                data-testid={`service-item-${g.key}-${i}`}
              >
                <Link to={`/services/${it.slug}`} data-testid={`service-link-${it.slug}`} className="group grid grid-cols-12 gap-4 py-7">
                  <span className="col-span-2 md:col-span-1 font-mono text-xs text-stone pt-2">{String(i + 1).padStart(2, "0")}</span>
                  <div className="col-span-9 md:col-span-10">
                    <h3 className="text-xl md:text-2xl font-medium tracking-tight transition-transform duration-500 group-hover:translate-x-2">{it.name}</h3>
                    <p className="mt-2 text-stone text-sm md:text-base leading-relaxed max-w-lg">{it.desc}</p>
                    <span className="mt-3 inline-block font-mono text-[11px] tracking-[0.22em] uppercase text-gold opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-[opacity,transform] duration-500">{t.services.discover} →</span>
                  </div>
                  <ArrowUpRight className="col-span-1 text-stone group-hover:text-gold group-hover:rotate-45 transition-[transform,color] duration-500" size={22} />
                </Link>
              </motion.li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
};
